import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getRiderSummary } from "../../api/adminApi";
import {
  Box, Card, CardContent, Typography, Select, MenuItem,
  TextField, Button, CircularProgress, Stack, Grid, Divider
} from "@mui/material";

import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import DownloadIcon from "@mui/icons-material/Download";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";

const COLORS = {
  "Online / UPI": "#2E7D32",       // Green
  "Cash on Delivery": "#1565C0",   // Blue
  "No Orders": "#ECE5DA"           // Grey
};

export default function RiderFinance({ riderId }) {
  const { id: urlRiderId } = useParams();
  const activeRiderId = riderId || urlRiderId;

  const [filter, setFilter] = useState("month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (filter === "custom" && (!startDate || !endDate)) return;
    if (!activeRiderId) return;

    try {
      setLoading(true);
      const params = { filter };
      if (filter === "custom") {
        params.start_date = startDate;
        params.end_date = endDate;
      }
      const res = await getRiderSummary(activeRiderId, params);
      setSummary(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter, startDate, endDate, activeRiderId]);

  useEffect(() => {
    if (filter !== "custom") fetchSummary();
  }, [filter, fetchSummary]);

  const handleExportExcel = () => {
    if (!summary || !summary.orders_export || summary.orders_export.length === 0) return;

    let csvContent = "Order ID,Date,Items Delivered,Amount (Rs),Payment Mode\n";
    summary.orders_export.forEach((o) => {
      const itemsList = o.items ? `"${o.items}"` : '"N/A"';
      csvContent += `${o.order_id},"${o.date}",${itemsList},${o.amount},"${o.payment_mode}"\n`;
    });

    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", `Rider_${activeRiderId}_Analytics_${filter}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const statBoxStyle = {
    p: 3,
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "left",
    border: "1px solid #ECE5DA",
    bgcolor: "#FFFDF8",
  };

  const onlineAmount = Number(summary?.online_amount) || 0;
  const offlineAmount = Number(summary?.offline_amount) || 0;
  const hasData = onlineAmount > 0 || offlineAmount > 0;

  const pieData = hasData
    ? [
      { name: "Online / UPI", value: onlineAmount },
      { name: "Cash on Delivery", value: offlineAmount },
    ].filter((item) => item.value > 0)
    : [{ name: "No Orders", value: 1 }];

  return (
    <Box sx={{ mb: 4, mt: 2 }}>
      {/* FILTER BOX */}
      <Card elevation={0} sx={{ borderRadius: "24px", border: "1px solid #ECE5DA", mb: 3, p: 1 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 }, "&:last-child": { pb: { xs: 2, sm: 3 } } }}>
          <Box display="flex" alignItems="center" justifyContent="center" gap={1.5} mb={3}>
            <TwoWheelerIcon sx={{ color: "#F8C332", fontSize: 28 }} />
            <Typography variant="h5" fontWeight="900" sx={{ color: "#3A2C23" }}>
              Order Analytics
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" justifyContent="center">
            <Select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{ borderRadius: "12px", minWidth: "160px", bgcolor: "#FFFFFF", width: { xs: "100%", sm: "auto" } }}
            >
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="month">Month</MenuItem>
              <MenuItem value="year">Year</MenuItem>
              <MenuItem value="custom">Custom</MenuItem>
            </Select>

            {filter === "custom" && (
              <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
                <TextField
                  type="date"
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#FFFFFF" }, flex: 1 }}
                />
                <TextField
                  type="date"
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: "#FFFFFF" }, flex: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={fetchSummary}
                  disabled={!startDate || !endDate || loading}
                  sx={{ borderRadius: "12px", bgcolor: "#3A2C23", color: "white" }}
                >
                  Go
                </Button>
              </Stack>
            )}

            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              disabled={!summary || summary.total_orders === 0}
              sx={{
                borderRadius: "12px",
                py: 1.2,
                px: 4,
                bgcolor: "#F8C332",
                color: "#3A2C23",
                fontWeight: "900",
                width: { xs: "100%", sm: "auto" },
                "&:hover": { bgcolor: "#F5B71D" },
              }}
            >
              EXCEL
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* METRICS & GRAPH */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress sx={{ color: "#F8C332" }} />
        </Box>
      ) : summary ? (
        <Card elevation={0} sx={{ borderRadius: "24px", border: "1px solid #ECE5DA", p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={3} alignItems="center">

            {/* LEFT SIDE: METRICS */}
            <Grid size={{ xs: 12, md: 5 }}>
              <Stack spacing={2.5}>
                <Box sx={statBoxStyle}>
                  <Typography variant="subtitle2" fontWeight="800" sx={{ color: "#A59D96", mb: 0.5 }}>
                    DELIVERED ORDERS
                  </Typography>
                  <Typography variant="h3" fontWeight="900" sx={{ color: "#3A2C23" }}>
                    {summary.total_orders}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    ...statBoxStyle,
                    bgcolor: "rgba(52, 168, 83, 0.05)",
                    borderColor: "rgba(52, 168, 83, 0.15)",
                    width: "100%",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="800" sx={{ color: "#2E7D32", mb: 0.5 }}>
                    TOTAL AMOUNT HANDLED
                  </Typography>
                  <Typography variant="h3" fontWeight="900" sx={{ color: "#1B5E20" }}>
                    ₹{summary.total_amount}
                  </Typography>

                  <Stack direction="row" spacing={2} sx={{ mt: 2.5, width: "100%" }}>
                    <Box sx={{ flex: 1, p: 1.5, borderRadius: "12px", bgcolor: "rgba(52, 168, 83, 0.1)" }}>
                      <Typography variant="caption" fontWeight="800" sx={{ color: "#2E7D32", display: "block", mb: 0.5 }}>
                        ONLINE / UPI
                      </Typography>
                      <Typography variant="h6" fontWeight="900" sx={{ color: "#1B5E20" }}>
                        ₹{summary.online_amount}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1, p: 1.5, borderRadius: "12px", bgcolor: "rgba(66, 133, 244, 0.08)" }}>
                      <Typography variant="caption" fontWeight="800" sx={{ color: "#1565C0", display: "block", mb: 0.5 }}>
                        OFFLINE / CASH
                      </Typography>
                      <Typography variant="h6" fontWeight="900" sx={{ color: "#0D47A1" }}>
                        ₹{summary.offline_amount}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            </Grid>

            {/* DIVIDER */}
            <Grid size={{ xs: 12, md: 1 }} sx={{ display: { xs: "none", md: "flex" }, justifyContent: "center" }}>
              <Divider orientation="vertical" sx={{ height: "220px", borderColor: "#ECE5DA" }} />
            </Grid>

            {/* RIGHT SIDE: DONUT CHART */}
            <Grid size={{ xs: 12, md: 6 }} sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Box sx={{ width: "100%", maxWidth: 360, height: 320, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ResponsiveContainer width="100%" height={320} minWidth={250}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="45%"
                      innerRadius={65}
                      outerRadius={95}
                      paddingAngle={hasData ? 4 : 0}
                      dataKey="value"
                      stroke="none"
                      isAnimationActive={true}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[entry.name] || "#ECE5DA"} />
                      ))}
                    </Pie>

                    {hasData && (
                      <Tooltip
                        formatter={(value) => [`₹${value}`, "Amount"]}
                        contentStyle={{
                          borderRadius: "12px",
                          border: "none",
                          boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        }}
                        itemStyle={{ fontWeight: 800 }}
                      />
                    )}

                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      iconType="circle"
                      wrapperStyle={{ fontWeight: 700, fontSize: "12px" }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </Grid>

          </Grid>
        </Card>
      ) : null}
    </Box>
  );
}